// §7 — HubSpot releases vs. uploaded Estimate documents, side by side.
// Read-only; the rule lives in releaseCheck.js.
import { checkEstimateReleases, parseMoney, fmtMoney } from "./releaseCheck";
import { fmtDate } from "./shared";

const money = (n) => (n === null || n === undefined ? "—" : fmtMoney(n));

export default function ReleaseReconciliation({ lineItems, sectionDocs, cacheTime, onOpenCoverSheet }) {
  const { releases, estimates, missing } = checkEstimateReleases(lineItems, sectionDocs);
  const ok = missing.length === 0;
  const unmatched = estimates.filter(e => !e.release);
  const cell = { padding: "7px 10px", borderBottom: "1px solid var(--cs-border)", fontSize: 12, verticalAlign: "top" };
  const head = { ...cell, fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "left" };

  return (
    <div style={{
      marginBottom: 20, border: `1px solid ${ok ? "#bbf7d0" : "#fca5a5"}`,
      background: ok ? "#f0fdf4" : "#fef2f2", borderRadius: 8, padding: "12px 14px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: ok ? "#166534" : "#991b1b" }}>
          {ok ? "✓ Estimates match the HubSpot releases" : "✗ Estimates don't match the HubSpot releases"}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)" }}>
          Releases from HubSpot{cacheTime ? ` · as of ${fmtDate(cacheTime)}` : ""}
          {onOpenCoverSheet && (
            <> · <button onClick={onOpenCoverSheet} style={{ background: "none", border: "none", padding: 0, color: "var(--cs-blue)", cursor: "pointer", fontSize: 11 }}>
              refresh on the Cover Sheet
            </button></>
          )}
        </div>
      </div>

      {releases.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--surface)", borderRadius: 6 }}>
          <thead>
            <tr>
              <th style={head}>Release</th>
              <th style={{ ...head, textAlign: "right" }}>Amount</th>
              <th style={head}>Status</th>
              <th style={head}>Matching estimate</th>
            </tr>
          </thead>
          <tbody>
            {releases.map(r => {
              const need = r.released && r.matchedDocs.length === 0;
              return (
                <tr key={r.key}>
                  <td style={cell}>{r.name}</td>
                  <td style={{ ...cell, textAlign: "right", fontWeight: 600 }}>{money(r.amount)}</td>
                  <td style={cell}>{r.released ? "Released" : "Forecasted"}</td>
                  <td style={{ ...cell, color: need ? "#b91c1c" : r.matchedDocs.length ? "#166534" : "var(--text3)" }}>
                    {r.matchedDocs.length
                      ? r.matchedDocs.map(d => `✓ ${d.FileName || d.Description} (${money(parseMoney(d.DocValue))})`).join(", ")
                      : need ? "✗ Required — upload an Estimate for this release" : "Optional"}
                  </td>
                </tr>
              );
            })}
            {unmatched.map(e => (
              <tr key={`u-${e.doc.TurnoverDocumentID}`}>
                <td style={{ ...cell, color: "#b91c1c" }} colSpan={4}>
                  ✗ Estimate “{e.doc.FileName || e.doc.Description}” {e.issue === "no_value" ? "has no value"
                    : e.issue === "mismatch" ? `(${money(e.value)}) does not equal ${e.taggedRelease.name} (${money(e.taggedRelease.amount)})`
                    : e.issue === "unknown_release" ? "is tagged to a release no longer on the HubSpot deal"
                    : `(${money(e.value)}) does not equal any release`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!ok && (
        <ul style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 12, color: "#991b1b" }}>
          {missing.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      <div style={{ marginTop: 8, fontSize: 11, color: "var(--text3)" }}>
        Upload each estimate under its release below, with Type “Estimate” and its total in Value ($). A released line item needs an Estimate that equals its amount exactly; this section can’t be completed until every one ties out.
      </div>
    </div>
  );
}
